import { Box, Button, Collapse, IconButton, Paper } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GridExpandMoreIcon } from '@mui/x-data-grid'
import React from 'react'
import { MvTypography } from '@/components/atoms/mv-typography'

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
  showDetails: boolean
}

type ErrorBoundaryProps = {
  children: React.ReactNode
}

const Img = styled('img')(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    height: 450,
    marginTop: theme.spacing(10)
  },
  [theme.breakpoints.down('md')]: {
    height: 400
  },
  [theme.breakpoints.up('lg')]: {
    marginTop: theme.spacing(20)
  }
}))

// Use a module-level Set of registered handlers instead of a single mutable ref.
// This survives HMR re-mounts and multiple boundary instances without going stale.
const globalErrorHandlers = new Set<(err: Error) => void>()

export function reportGlobalError(error: Error) {
  if (globalErrorHandlers.size === 0) {
    // Boundary not yet mounted — queue until it is
    pendingErrors.push(error)
    return
  }
  globalErrorHandlers.forEach(handler => handler(error))
}

const pendingErrors: Error[] = []

export class GlobalErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    error: null,
    showDetails: false
  }

  private handleGlobalError = (error: Error) => {
    this.setState({ hasError: true, error })
  }

  componentDidMount() {
    globalErrorHandlers.add(this.handleGlobalError)

    // Flush any errors that fired before the boundary mounted
    if (pendingErrors.length > 0) {
      const first = pendingErrors.shift()!
      this.setState({ hasError: true, error: first })
    }
  }

  componentWillUnmount() {
    globalErrorHandlers.delete(this.handleGlobalError)
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('💥 React Error:', error, info)
  }

  toggleDetails = () => {
    this.setState(s => ({ showDetails: !s.showDetails }))
  }

  reload = () => {
    window.location.reload()
  }

  render() {
    const { hasError, error, showDetails } = this.state

    if (!hasError) {
      return this.props.children
    }

    return (
      <Box
        sx={{
          minHeight: '80vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Paper
          elevation={6}
          sx={{
            width: '100%',
            p: 8,
            borderRadius: 3,
            textAlign: 'center',
            gap: 5
          }}
        >
          <MvTypography size='TITLE_LG'>Something went wrong</MvTypography>

          <MvTypography size='BODY_LG_NORMAL'>
            We're sorry — an unexpected error occurred. Our system has stopped to prevent data corruption.
          </MvTypography>

          <Img height='500' alt='error-illustration' src='/images/pages/404.png' />

          <Box sx={{ mt: 2 }}>
            <Button variant='contained' size='medium' onClick={this.reload} sx={{ mb: 2 }}>
              Reload Application
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box
              onClick={this.toggleDetails}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'text.secondary',
                userSelect: 'none'
              }}
            >
              <MvTypography size='BODY_LG_NORMAL'>
                {showDetails ? 'Hide error details' : 'Show error details'}
              </MvTypography>

              <IconButton size='small'>
                <GridExpandMoreIcon
                  sx={{
                    transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: '0.2s'
                  }}
                />
              </IconButton>
            </Box>

            <Collapse in={showDetails}>
              <Paper
                variant='outlined'
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: '#0f172a',
                  color: '#e5e7eb',
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  fontSize: 13,
                  maxHeight: 300,
                  overflow: 'auto'
                }}
              >
                <strong>{error?.name}</strong>
                <br />
                {error?.message}
                <br />
                <br />
                {error?.stack}
              </Paper>
            </Collapse>
          </Box>
        </Paper>
      </Box>
    )
  }
}
